import React from 'react';
import moment from 'moment';
import { AddCircle, Schedule, DeleteOutlineRounded, Save } from '@material-ui/icons';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { DeliveryTimeRangeConfigForm } from 'forms/schemas/VenueDeliveryConfigForm';
import 'styles/components/AddOrRemoveTimeRange.scss';
import AddOrRemoveDeliveryTimeRangeTableProps from 'types/forms/AddOrRemoveDeliveryTimeRangeTableProps';
import { DeliveryTimeRangeConfig } from 'types/models/VenueDeliveryConfig';

export default ({
  onAddRow,
  onUpdateRow,
  onDeleteRow,
  values,
  inputName,
  setFieldValue,
}: AddOrRemoveDeliveryTimeRangeTableProps): JSX.Element => {
  const { t } = useTranslation();

  const arrayZeroAlwaysAtEnd = (array: DeliveryTimeRangeConfig[]) => {
    const elementoCero = array.find((item) => item.id === 0);
    const arraySinCero = array.filter((item) => item.id !== 0);
    let finalArray = [];
    if (elementoCero) {
      finalArray = [...arraySinCero, elementoCero];
    } else {
      finalArray = array;
    }

    return finalArray;
  };

  const Row = ({ config }: { config?: DeliveryTimeRangeConfig }): JSX.Element => {
    const onlyHour = (value: string) => moment(value, ['HH:mm:ss', 'H:mm', 'HH:mm']).format('HH:mm');
    const [timeRangeError, setTimeRangeError] = React.useState<string | null>(null);
    const formik = useFormik({
      initialValues: {
        id: config?.id ?? 0,
        fromHour: config?.toHour ? onlyHour(config.fromHour as string) : '',
        toHour: config?.toHour ? onlyHour(config.toHour as string) : '',
      },
      validationSchema: DeliveryTimeRangeConfigForm(t),
      onSubmit: () => console.log('test'),
    });

    const timeToMinutes = (timeString: string) => {
      // Desestructura la cadena en horas, minutos y segundos.
      const [hours, minutes] = onlyHour(timeString).split(':').map(Number);
      // Calcula los minutos totales
      return hours * 60 + minutes;
    };

    const hasTimeOverlap = (
      existingRanges: DeliveryTimeRangeConfig[],
      newFromHour: string,
      newToHour: string,
      id?: number,
    ) => {
      // 1. Convierte el nuevo rango a minutos.
      const newStart = timeToMinutes(newFromHour);
      const newEnd = timeToMinutes(newToHour);

      // Valida que el rango sea válido antes de la comparación
      if (newStart >= newEnd) {
        setTimeRangeError(t('venueDeliveryConfig-form-fromHour-upper-than-toHour'));
        return true;
        // Devolvemos true para que hacer de cuenta que hasTimeOverlap simule que dio error
      }

      const isOverlapping = existingRanges.some((existingRange) => {
        // Retornamos false para ignorar el nuevo rango que queremos agregar
        if (existingRange.id === 0) return false;

        // No se tiene que comparar consigo mismo si es que estoy editando
        if (id && existingRange.id === id) return false;

        const existingStart = timeToMinutes(existingRange.fromHour);
        const existingEnd = timeToMinutes(existingRange.toHour);

        // Lógica de Superposición:
        // Hay superposición si:
        // - El nuevo rango empieza antes de que el existente termine (newStart < existingEnd)
        // Y
        // - El nuevo rango termina después de que el existente empiece (newEnd > existingStart)

        // **IMPORTANTE sobre la Inclusión/Exclusión de Bordes (Endpoints):**
        // Si quieres permitir que un rango termine EXACTAMENTE cuando el otro empieza
        // (ej: [01:00-02:00] y [02:00-03:00]),
        // debes usar '<' y '>' en lugar de '<=' y '>='.
        // El código a continuación asume que [01:00-02:00] y [02:00-03:00] NO se superponen.
        return newStart < existingEnd && newEnd > existingStart;
      });

      if (isOverlapping) setTimeRangeError(t('venueDeliveryConfig-form-overlapping-range-time'));
      return isOverlapping;
    };

    return (
      <>
        <div className="row-container-range">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px',
              background: '#f5f5f5',
              borderRadius: '10px',
              flexWrap: 'wrap',
            }}
          >
            {/* START DATE */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '6px 10px',
                background: 'white',
                cursor: 'pointer',
                minWidth: '200px',
              }}
            >
              <Schedule style={{ color: '#666' }} />
              <span style={{ color: formik.values.fromHour ? '#000' : '#999' }}>
                {formik.values.fromHour ? formik.values.fromHour : t('venueDeliveryConfig-form-init-hour')}
              </span>
              <input
                id="dateInputModified"
                type="time"
                name="fromHour"
                value={formik.values.fromHour as string}
                onFocus={() => setTimeRangeError(null)}
                onChange={(event) => {
                  formik.setFieldValue('fromHour', event.target.value);
                }}
                style={{
                  position: 'absolute',
                  minWidth: 200,
                  opacity: 0,
                }}
              />
            </div>

            <span style={{ color: '#666' }}>—</span>

            {/* END DATE */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '6px 10px',
                background: 'white',
                cursor: 'pointer',
                minWidth: '200px',
              }}
            >
              <Schedule style={{ color: '#666' }} />
              <span style={{ color: formik.values.toHour ? '#000' : '#999' }}>
                {formik.values.toHour ? formik.values.toHour : t('venueDeliveryConfig-form-limit-hour')}
              </span>
              <input
                id="dateInputModified"
                type="time"
                name="toHour"
                value={formik.values.toHour}
                min={formik.values.fromHour}
                onFocus={() => setTimeRangeError(null)}
                onChange={(event) => formik.setFieldValue('toHour', event.target.value)}
                style={{
                  position: 'absolute',
                  minWidth: 200,
                  opacity: 0,
                }}
              />
            </div>
          </div>

          <div className="buttons-container">
            {config?.id ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (!formik.values.id) return;
                    const isOverlapping = hasTimeOverlap(
                      values,
                      formik.values.fromHour,
                      formik.values.toHour,
                      formik.values.id,
                    );
                    if (!isOverlapping) {
                      onUpdateRow(formik.values);
                    }
                  }}
                >
                  {t('listFDConfigs-page-edit')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    onDeleteRow(formik.values);
                  }}
                >
                  <DeleteOutlineRounded />
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const isOverlapping = hasTimeOverlap(values, formik.values.fromHour, formik.values.toHour);
                  if (!isOverlapping) {
                    onAddRow(formik.values);
                  }
                }}
              >
                <Save />
              </Button>
            )}
          </div>
        </div>
        {timeRangeError && <div className="time-range-error">{timeRangeError}</div>}
      </>
    );
  };

  return (
    <div className="container">
      <div className="title-container">
        <div className="add-container-range">
          <Button
            variant="contained"
            color="primary"
            endIcon={<AddCircle />}
            onClick={() => {
              if (!values.some(({ id }) => id === 0)) {
                values.push({ id: 0, fromHour: '', toHour: '' });
                setFieldValue(inputName, [...values]);
              }
            }}
          >
            {t('venueDeliveryConfig-form-add-time-range-config')}
          </Button>
        </div>
      </div>
      {values && values.length > 0 && (
        <>
          {arrayZeroAlwaysAtEnd(values).map((config, index) => (
            <Row key={index} config={config} />
          ))}
        </>
      )}
    </div>
  );
};
